import { useState, useMemo, useRef } from "react"
import { useRequest } from "ahooks";
import { Card, Button, message, Modal } from "antd";
import { EditForm } from "../../../components/editForm";
import { Table, DEFAULT_PAGE_DATA } from "../../../components/table";
import { DEFAULT_QUERY_DATA, generateQueryFormConfig, generateTableColumns } from "./config";
import { AmountServer } from "../../../server";
import { Operation } from "./operation";

const server = AmountServer.InternationalRemittanceLimitList;
const creditedTimesServer = AmountServer.InternationalRemittanceCreditedTimesList;
const countryServer = AmountServer.InternationalRemittanceCountriesList;

export const InternationalRemittanceLimitList = () => {
    const operationRef: any = useRef();
    const [loading, setLoading] = useState<boolean>(false);
    const [queryData, setQueryData] = useState<any>({ ...DEFAULT_QUERY_DATA });
    const [tableData, setTableData] = useState<any[]>([]);
    const [pageData, setPageData] = useState<any>({ ...DEFAULT_PAGE_DATA });
    const [options, setOptions] = useState<any>({
        creditedTimeOptions: [],
        countryOptions: [],
    });

    // 获取通道时效和国家下拉列表
    useRequest(() => Promise.all([
        creditedTimesServer.list({ Page: 1, PageSize: 50 }),
        countryServer.list({ Page: 1, PageSize: 50 }),
    ]), {
        onSuccess: (res: any) => {
            // 处理通道时效 options
            const creditedTimeOptions = res[0]?.List?.map((item: any) => ({
                id: item.Id,
                // 拼接：Channel.Name + Name
                name: `${item.Channel?.Name || ''} - ${item.Name}`
            })) || [];
    
            // 处理国家 options
            const countryOptions = res[1]?.List?.map((item: any) => ({
                id: item.Id,
                name: item.NameCn,
            })) || [];
    
            setOptions({
                creditedTimeOptions,
                countryOptions,
            });
        }
    });

    //获取表格数据
    const { run, refresh } = useRequest(server.list, {
        defaultParams: [{ ...pageData }],
        onBefore: () => setLoading(true),
        onFinally: () => setLoading(false),
        onError: () => {
            setTableData([]);
            setPageData({ ...pageData, Total: 0 });
        },
        onSuccess: (res: any) => {
            const { Total, List, Page, PageSize } = res;
            setTableData(List);
            setPageData({ Page, PageSize, Total });
        }
    });

    // 删除请求
    const { run: runDelete, loading: deleteLoading } = useRequest(server.delete, {
        manual: true,
        onSuccess: () => {
            message.success('删除成功');
            refresh();
        },
        onError: (error) => {
            message.error('删除失败: ' + error.message);
        }
    });

    const getQueryFormConfig = useMemo(() => {
        return generateQueryFormConfig(options);
    }, [options]);

    const getTableColumns = useMemo(() => {
        const baseColumns = generateTableColumns();
        // 添加操作列
        return [
            ...baseColumns,
            {
                title: "操作",
                key: "action",
                fixed: "right",
                width: 150,
                render: (_: any, record: any) => (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button 
                            type="link" 
                            size="small"
                            onClick={() => {
                                operationRef.current?.changeVisible(record, 'update');
                            }}
                        >
                            编辑
                        </Button>
                        <Button 
                            type="link" 
                            danger
                            size="small"
                            loading={deleteLoading}
                            onClick={() => {
                                Modal.confirm({
                                    title: '确认删除',
                                    content: `确定要删除该限额配置吗？`,
                                    onOk: () => {
                                        runDelete({ Id: record.Id });
                                    }
                                });
                            }}
                        >
                            删除
                        </Button>
                    </div>
                ),
            }
        ];
    }, [deleteLoading]);

    return (
        <div>
            <Card>
                <div className="flex space-between align-center ">
                    <EditForm
                        componentId="international-remittance-limit-query-form"
                        loading={loading}
                        formAttrsProps={{
                            layout: 'inline',
                        }}
                        formData={queryData}
                        onDataChange={setQueryData}
                        formConfig={getQueryFormConfig}
                        onQuery={() => {
                            run({ ...DEFAULT_PAGE_DATA, ...queryData });
                        }}
                        onReset={() => {
                            setQueryData({ ...DEFAULT_QUERY_DATA });
                            run({ ...DEFAULT_QUERY_DATA, ...DEFAULT_PAGE_DATA });
                        }}
                    />
                    <Button 
                        type="primary" 
                        onClick={() => {
                            operationRef.current?.changeVisible({}, 'create');
                        }}
                    >
                        新增
                    </Button>
                </div>
            </Card>
            <Card className="mt20">
                <Table
                    loading={loading}
                    dataSource={tableData}
                    columns={getTableColumns}
                    rowKey="Id"
                    scroll={{
                        x: 'max-content'
                    }}
                    page={pageData}
                    onPageChange={(Page: number, PageSize: number) => {
                        const currentPage = { ...pageData, Page, PageSize };
                        run({ ...currentPage, ...queryData });
                    }}
                />
            </Card >
            <Operation ref={operationRef} onSuccess={refresh} options={options} />
        </div>
    );
}
